import isEqual from 'lodash/isEqual';
import { take } from 'rxjs/operators';

import { Component, Injector } from '@angular/core';

import { GroupsMultiSelectFilterBaseComponent } from './base.component';
import { ISelectOption } from '@app/misc/hierarchy-multi-select-with-counter/hierarchy-multi-select-with-counter.component';
import { AdminFeatureFlagService } from '@app/services/feature-flags/admin';

import { IGroup } from '@app/admin/groups/admin-group.service';
import { GroupsFilter } from '@app/services/groups-filter';

@Component({
  selector: 'global-groups-multi-select-filter',
  styleUrls: ['./base.component.scss'],
  templateUrl: './base.component.html',
})
export class GlobalGroupsMultiSelectFilterComponent extends GroupsMultiSelectFilterBaseComponent {
  /** Map of group IDs to the groups they represent. */
  protected groupsMap: Map<number, IGroup> = new Map();

  constructor(
    injector: Injector,
    adminFeatureFlagService: AdminFeatureFlagService,
    private groupsFilter: GroupsFilter,
  ) {
    super(injector, adminFeatureFlagService);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.groupsFilter.resume();
    this.destroyed$.pipe(take(1))
      .subscribe(() => this.groupsFilter.suspend());

    if (this.phxImplementation) this.selectedOptions = this.groupsFilter._storedValue || [];
    else this.parentSelectedGroupIds$.next(this.groupsFilter._storedValue || []);
  }

  // this method will be removed in feature flag cleanup
  protected onGroupsUpdated(groups: IGroup[], parentSelectedGroupIds: Set<number>): void {
    const selectedGroupIds: Set<number> = this.unionSet(parentSelectedGroupIds, this.groupsFilter.value);
    this.options$.next(groups.map((group: IGroup) => this.transformGroupToOptions(group, selectedGroupIds)));

    this.groupsMap = this.mapGroups(groups);
  }

  protected onSelectedGroupIdsUpdated(selectedGroupIds: Set<number>): void {
    if (!this.hasChanged) return;

    const selectedGroupIdsArray: number[] = Array.from(selectedGroupIds);
    if (!isEqual(selectedGroupIdsArray, this.groupsFilter.value)) {
      this.groupsFilter.update(selectedGroupIdsArray);
      this.groupsFilter.areSelectedGroupExempted = this.areAllSelectedGroupsExempted(selectedGroupIds, this.groupsMap);
    }
  }

  /**
   * Recursively create a mapping of group IDs to the groups they represent.
   * @param groups The group hierarchy.
   * @returns The mapping.
   */
  private mapGroups(groups: IGroup[]): Map<number, IGroup> {
    return groups.reduce((groupMap: Map<number, IGroup>, group: IGroup) => {
      groupMap.set(group.id, group);
      if (group.children) {
        this.mapGroups(group.children).forEach((child: IGroup, childId: number) => {
          groupMap.set(childId, child);
        });
      }
      return groupMap;
    }, new Map());
  }

  /**
   * Create the union of a collection of sets and arrays.
   * @param sets A collection of sets and arrays.
   * @returns A set containing all the elements in any input set.
   */
  private unionSet<Type>(...sets: Array<Set<Type> | Type[]>): Set<Type> {
    const union: Set<Type> = new Set();
    sets.forEach((set: Set<Type>) => {
      if (set) {
        return set.forEach((item: Type) => union.add(item));
      }
    });
    return union;
  }

  /**
   * Select any options whose keys are in a set of pre-selected group IDs.
   * @param options The options.
   * @param selectedGroupIds The pre-selected group IDs.
   * @returns The updated options.
   */
  private selectOptions(options: ISelectOption[], selectedGroupIds: Set<number>): ISelectOption[] {
    options.forEach((option: ISelectOption) => {
      if (selectedGroupIds.has(parseInt(option.key, 10))) {
        option.isPreSelected = true;
      }
      if (option.children) {
        this.selectOptions(option.children, selectedGroupIds);
      }
    });
    return options;
  }

  /**
   * Determine whether all groups in a set of selected groups are ELD-exempt.
   * @param selectedGroupIds The IDs of the selected groups.
   * @param groupsMap A mapping of group IDs to the groups they represent.
   * @returns True, if and only if all selected groups are ELD-exempt.
   */
  private areAllSelectedGroupsExempted(selectedGroupIds: Set<number>, groupsMap: Map<number, IGroup>): boolean | null {
    if (selectedGroupIds.size === 0 || selectedGroupIds.size === groupsMap.size) {
      return null;
    } else {
      let allExempted: boolean = true;
      selectedGroupIds.forEach((groupId: number) => {
        if (allExempted) {
          const group: IGroup = groupsMap.get(groupId);
          allExempted = group.operatingEldModes === 'exempt';
        }
      });
      return allExempted;
    }
  }
}
